import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ILogReport } from '../../types/report-log.type';
import styled from 'styled-components';
import useReportLog from '../../../../hooks/useReportLog';
import { calculateGpsSummary, latToDMS, longToDMS } from '../helperFunction';
import {
  buildVesselTrackingData,
  VesselTrackingData,
} from '../../pdf/reportPdfData';
import dayjs from 'dayjs';
import {
  EFormatCurrentDate,
  formatCurrentDate,
} from '../../../../helpers/formatCurrentDate';
import { useVesselTrackingGps } from '../vesselTracking/useVesselTrackingGps';

interface IProps {
  // calculated by the backend for the PDF page (GET /report-log/pdf-data)
  precomputed?: VesselTrackingData & {
    gpsSummary: { totalCruise: string; totalDistance: string; avgSpeed: string };
  };
}

type TMapPoint = [number, number];

const VesselTrackingStatic: React.FC<IProps> = ({ precomputed }) => {
  const { loadingLog, logs, speedUnit, distanceUnit, cruiseUnit } =
    useReportLog();
  const { gpsLogs, loading: loadingTrackingGps } = useVesselTrackingGps({
    disabled: Boolean(precomputed),
  });
  const tracking = useMemo(
    () => precomputed ?? buildVesselTrackingData(gpsLogs),
    [precomputed, gpsLogs]
  );
  const [mapImages, setMapImages] = useState<{
    mapLeft?: string;
    mapRight?: string;
  }>({
    mapLeft: undefined,
    mapRight: undefined,
  });
  const [mapLoaded, setMapLoaded] = useState({
    left: false,
    right: false,
  });
  const leftMapImageRef = useRef<HTMLImageElement>(null);
  const rightMapImageRef = useRef<HTMLImageElement>(null);

  const isMapReady = Boolean(
    mapImages.mapLeft && mapImages.mapRight && mapLoaded.left && mapLoaded.right
  );

  const currentPath: TMapPoint[] = tracking.currentPath;

  const summary = useMemo(() => {
    let firstData = {
      position: '-',
      time: '-',
    };
    let lastData = {
      position: '-',
      time: '-',
    };
    if (tracking.first) {
      firstData = {
        position: `${latToDMS(tracking.first.latitude)}, ${longToDMS(
          tracking.first.longitude
        )}`,
        time: formatCurrentDate(
          tracking.first.timestamp * 1000,
          EFormatCurrentDate.DEFAULT_SHORT_MONTH
        ),
      };
    }
    if (tracking.last) {
      lastData = {
        position: `${latToDMS(tracking.last.latitude)}, ${longToDMS(
          tracking.last.longitude
        )}`,
        time: formatCurrentDate(
          tracking.last.timestamp * 1000,
          EFormatCurrentDate.DEFAULT_SHORT_MONTH
        ),
      };
    }
    let data = {
      firstData,
      lastData,
      totalCruise: '0',
      totalDistance: '0',
      avgSpeed: '0',
    };
    if (precomputed) {
      data = { ...data, ...precomputed.gpsSummary };
    } else if (logs && logs.gps) {
      const res = calculateGpsSummary(logs, {
        distanceUnit,
        speedUnit,
        cruiseUnit,
      });
      data = {
        ...data,
        totalCruise: res.totalCruise as string,
        totalDistance: res.totalDistance as string,
        avgSpeed: res.avgSpeed as string,
      };
    }

    return data;
  }, [cruiseUnit, distanceUnit, tracking, logs, speedUnit, precomputed]);

  async function generateImage(data: {
    withoutPath?: boolean;
    currentPath: TMapPoint[];
  }) {
    try {
      const zoom = data.withoutPath ? 8 : 11;
      const lat = data.currentPath[data.currentPath.length - 1][0];
      const lng = data.currentPath[data.currentPath.length - 1][1];
      const path = `color:0xff0000ff|weight:5|${data.currentPath.join('|')}`;
      const constructUrl = data.withoutPath
        ? `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=${zoom}&size=535x300&markers=color:red%7C${lat},${lng}&maptype=hybrid&key=${process.env.REACT_APP_GMAPS_API_KEY}`
        : `https://maps.googleapis.com/maps/api/staticmap?size=535x300&markers=color:red%7C${lat},${lng}${
            data.currentPath && data.currentPath.length > 0
              ? `&path=color:yellow|weight:3|${path}`
              : `&zoom=${zoom}`
          }&maptype=hybrid&key=${
            process.env.REACT_APP_GMAPS_API_KEY
          }&sensor=false`;
      const res = await axios({
        method: 'post',
        url: `https://beta.ramus.asia/api-signature/generate-signature`,
        data: {
          url: constructUrl,
          privateKey: process.env.REACT_APP_GMAPS_PRIVATE_KEY,
        },
      });

      console.log('res generate image 2', res.data);
      return res.data.result;
    } catch (error: any) {
      console.error('failed generate static map', error);
    }
  }

  useEffect(() => {
    if (!mapImages.mapLeft || !mapImages.mapRight) {
      return;
    }

    const isImageReady = (image: HTMLImageElement | null) =>
      Boolean(image && image.complete && image.naturalWidth > 0);

    setMapLoaded({
      left: isImageReady(leftMapImageRef.current),
      right: isImageReady(rightMapImageRef.current),
    });
  }, [mapImages.mapLeft, mapImages.mapRight]);

  useEffect(() => {
    let ignore = false;

    const renderMapImage = async () => {
      setMapLoaded({
        left: false,
        right: false,
      });

      const mapLeft = await generateImage({ currentPath });
      const mapRight = await generateImage({ currentPath, withoutPath: true });

      if (ignore) {
        return;
      }

      setMapImages({
        mapLeft,
        mapRight,
      });
    };

    if (!loadingLog && !loadingTrackingGps && currentPath.length > 0) {
      renderMapImage();
    } else {
      setMapImages({
        mapLeft: undefined,
        mapRight: undefined,
      });
      setMapLoaded({
        left: false,
        right: false,
      });
    }

    return () => {
      ignore = true;
    };
  }, [currentPath, loadingLog, loadingTrackingGps]);

  const tableColumnsHorizontal = ['', 'Position', 'Time'];

  return (
    <>
      <div
        id='map-images-ready'
        data-ready={isMapReady ? 'true' : 'false'}
        style={{ display: 'none' }}
      />
      <StaticMapContainer>
        <img
          ref={leftMapImageRef}
          alt='map-left'
          style={{ height: 300 }}
          id='map-image-left'
          src={mapImages.mapLeft}
          onLoad={() => {
            setMapLoaded((prev) => ({
              ...prev,
              left: true,
            }));
          }}
          onError={() => {
            setMapLoaded((prev) => ({
              ...prev,
              left: false,
            }));
          }}
        />
        <img
          ref={rightMapImageRef}
          alt='map-right'
          style={{ height: 300 }}
          id='map-image-right'
          src={mapImages.mapRight}
          onLoad={() => {
            setMapLoaded((prev) => ({
              ...prev,
              right: true,
            }));
          }}
          onError={() => {
            setMapLoaded((prev) => ({
              ...prev,
              right: false,
            }));
          }}
        />
      </StaticMapContainer>

      <table className='table-app'>
        <tbody>
          <tr>
            {tableColumnsHorizontal.map((item, idx) => (
              <td key={idx}>{item}</td>
            ))}
          </tr>
          <tr>
            <td>Start</td>
            <td>{summary.firstData.position}</td>
            <td>{summary.firstData.time}</td>
          </tr>
          <tr>
            <td>End</td>
            <td>{summary.lastData.position}</td>
            <td>{summary.lastData.time}</td>
          </tr>
          <tr>
            <td>Total Cruise</td>
            <td colSpan={2}>
              {summary.totalCruise} {cruiseUnit}
            </td>
          </tr>
          <tr>
            <td>Average Speed</td>
            <td colSpan={2}>
              {summary.avgSpeed} {speedUnit}
            </td>
          </tr>
          <tr>
            <td>Total Distance</td>
            <td colSpan={2}>
              {summary.totalDistance} {distanceUnit}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

const StaticMapContainer = styled.div`
  display: grid;
  align-items: center;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
  margin-top: 20px;
  margin-bottom: 20px;
  img {
    width: 100%;
  }
`;

export default VesselTrackingStatic;
